import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import React, { FunctionComponent, useEffect, useState } from "react";
import type { IpfModule } from "../..";
import { MessageType } from "../../enums.generated";
import { getMessageIcon } from "../../functions/get-message-icon";
import { TYPES } from "../../ioc/types";
import { ConfirmationPromptOptions } from "../../models/app";
import { Guid } from "../../util/Guid";
import { useInject } from "../hooks/useInject";
import { Alert } from "./Alert";

/**
 * Confirmation prompt component properties, enabling configuration of the prompt content and action buttons.
 */
export type ConfirmationPromptProps = { options: ConfirmationPromptOptions };

const getPromptCssClass = (type?: MessageType) => {
	let cssClass: string | undefined;
	switch (type) {
		case MessageType.Error:
			cssClass = "prompt-danger";
			break;
		case MessageType.Success:
			cssClass = "prompt-success";
			break;
		case MessageType.Warning:
			cssClass = "prompt-warning";
			break;
		case MessageType.Warning:
			cssClass = "prompt-info";
			break;
	}
	return cssClass;
}

/**
 * Confirmation prompt component, outputs a Material UI prompt, display a configurable range of messages and allows configuration of confirm and cancel action buttons.
 * @param props Prompt properties, enabling configuration of the prompt content and buttons.
 * @see Dialog
 */
export const ConfirmationPrompt: FunctionComponent<ConfirmationPromptProps> = (props: ConfirmationPromptProps) => {
	const [open, setOpen] = useState(props.options.autoOpen ?? false);
	const ipfModule = useInject<IpfModule>(TYPES.IpfModule);

	useEffect(() => {

		// once rendered call the onRendered handler
		if (props.options.onRendered) {
			props.options.onRendered(open);
		}
	});

	// confirm button handler
	const handleConfirm = props.options.confirmOption?.pageHandler ? undefined : () => {

		if (props.options.confirmOption?.handler) {
			props.options.confirmOption.handler();
		}

		setOpen(false);
	};

	// cancel button handler
	const handleClose = props.options.cancelOption?.pageHandler ? undefined : (reason: string) => {

		if (ipfModule.context.clientDebugEnabled) {

			console.log("ConfirmationPrompt.handleClose called with reason: %s", reason);
		}

		if (props.options.closeOnBackdropClick === false && reason == "backdropClick") {

			return;
		}

		if (props.options.cancelOption?.handler) {
			props.options.cancelOption.handler();
		}

		setOpen(false);
	};

	props.options.open = () => {
		setOpen(true);
	};

	props.options.close = () => {
		setOpen(false);
	};

	// if there is a message type then build an icon element
	const icon = props.options.type ? <i className={getMessageIcon(props.options.type)}></i> : null;

	// create confirm button element
	const confirmType = props.options.confirmOption?.pageHandler ? "submit" : undefined;
	const confirmElement = props.options.confirmOption ? <Button key="confirm" onClick={handleConfirm} formAction={props.options.confirmOption.pageHandler} color={props.options.confirmOption.style ?? "primary"} variant="contained" type={confirmType} autoFocus>
		{props.options.confirmOption.text ?? "Confirm"}
	</Button> : null;

	// create cancel button element
	const cancelElement = props.options.cancelOption ? <Button key="cancel" onClick={() => { if (handleClose) handleClose("cancelClick"); }} formAction={props.options.cancelOption.pageHandler} color={props.options.cancelOption.style ?? "secondary"} variant="contained">
		{props.options.cancelOption.text}
	</Button> : null;

	// build the list of buttons
	let buttons = new Array<JSX.Element>();
	if (cancelElement) {
		buttons.push(cancelElement);
	}
	if (confirmElement) {
		buttons.push(confirmElement);
	}
	if (props.options.reverseButtons === true) {
		buttons = buttons.reverse();
	}

	// place buttons within dialog actions element
	const buttonsElement = buttons.length ?
		<DialogActions>
			{buttons}
		</DialogActions> : null;

	// if there are messages then build dialog content containing a set of messages as alert elements
	let content: JSX.Element;
	if (props.options.messages) {

		const messageElements = props.options.messages.map(message => {
			if (!message.id) {
				message.id = Guid.newGuid();
			}
			return <Alert key={message.id} options={message} />;
		});

		content = <DialogContent>{props.options.content ? <div id="dialog-content"></div> : <div>
			{messageElements}
		</div>}</DialogContent>;
	}
	// otherwise set dialogue content text
	else {

		const textElement = props.options.renderTextAsHtml ? <span dangerouslySetInnerHTML={{ __html: props.options.text }}></span> : <span>{props.options.text}</span>;
		content = <DialogContent>{props.options.content ? <div id="dialog-content"></div> : <DialogContentText id="alert-dialog-description">
			{!props.options.title ? icon : undefined}{textElement}
		</DialogContentText>}</DialogContent>;
	}

	let container: HTMLElement | null;

	// if inserting a content element into the prompt then set the prompt container as any parent form
	// this is important and enables the asp-page-handler buttons to work when pressed
	if (props.options.content) {
		container = props.options.content.closest("form");
	}
	// if there's a target element ID then get that element and set as the prompt container
	else if (props.options.targetElementId) {
		container = document.getElementById(props.options.targetElementId);
	}
	else {
		container = null;
	}

	// now output MUI Dialog
	return (
		<div>
			<Dialog
				open={open}
				onClose={(event, reason) => {
					if (handleClose) handleClose(reason);
				}}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
				className={getPromptCssClass(props.options.type)}
				maxWidth="lg"
				closeAfterTransition={props.options.closeAfterTransition}
				transitionDuration={props.options.closeAfterTransition ? 0 : undefined}
				container={container}
			>
				<DialogTitle id="alert-dialog-title">{props.options.title ? icon : undefined}{props.options.title}</DialogTitle>
				{content}
				{buttonsElement}
			</Dialog>
		</div>
    );
}